import React from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { isArrayEmpty } from 'utils/arrayUtils';
import { MadadAgentCard, MadadAgencyCard } from 'components/madad/entities';
import { H2, SmallText } from 'ds/components/typography';
import { AgentsCarousel } from './AgentsCarousel';
import { AgenciesCarousel } from './AgenciesCarousel';
import { LinkToMadadLanding } from './LinkToMadadLanding';
import { SpacingBlock } from 'components/spacing-block';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { variantToImagePath } from 'screens/MadadPage/card/consts';


const madadInfoBadgePath = 'madad/madad_preview_2026.png';

const Root = styled.div`
  ${({ theme }) => `
    background-color: ${theme.colors.neutrals.grey9};
    padding-top: ${theme.spacing(5)};
    padding-bottom: ${theme.spacing(8)};
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    padding-top: ${theme.spacing(5)};
    padding-bottom: ${theme.spacing(3)};
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => `
    margin-bottom: ${theme.spacing(5)};
  `}
`;

const HeaderBadge = styled.div`
  ${({ theme }) => `
    margin-left: ${theme.spacing(3)};
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    margin-left: ${theme.spacing(2)};
  `}
`;

const BadgeImg = styled.img`
  display: block;
`;

const HeaderDetails = styled.div``;

const HeaderTitle = styled(H2)`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey1};
  `}
`;
HeaderTitle.defaultProps = { weight: 'bold' };

const HeaderSubTitle = styled(SmallText)`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey3};
    padding-top: ${theme.spacing(0.5)};
  `}
`;

const ContentSection = styled.div``;

const ContentSectionTitle = styled(SmallText)`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey3};
    margin-bottom: ${theme.spacing(2)};
  `}
`;
ContentSectionTitle.defaultProps = { weight: 'bold' };

interface LocalWinnersProps {
  agents: MadadAgentCard[];
  agencies: MadadAgencyCard[];
  area?: 'city' | 'hood';
}

const BADGE_SIZE = 67;
const BADGE_RATIO = 2;
const BADGE_IMAGE_SIZE = BADGE_SIZE * BADGE_RATIO;

export const LocalWinners: React.FC<LocalWinnersProps> = props => {
  const { agents, agencies, area } = props;
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;
  const constructImageURI = useImageUrlBuilder();
  const imageURL = constructImageURI(madadInfoBadgePath, { height: BADGE_IMAGE_SIZE, width: BADGE_IMAGE_SIZE });

  const hasAgents = !isArrayEmpty(agents);
  const hasAgencies = !isArrayEmpty(agencies);

  if (!hasAgents && !hasAgencies) return null;

  return (
    <Root>
      <Header>
        <HeaderBadge>
          <BadgeImg src={imageURL} height={BADGE_SIZE} width={BADGE_SIZE} />
        </HeaderBadge>
        <HeaderDetails>
          <HeaderTitle>{t('madad.localWinners.header', { isMobile })}</HeaderTitle>
          <HeaderSubTitle>{t('madad.localWinners.header.clarification', { isMobile })} </HeaderSubTitle>
        </HeaderDetails>
      </Header>
      {hasAgencies ? (
        <ContentSection>
          <AgenciesCarousel agencies={agencies} variantToImagePath={variantToImagePath} />
        </ContentSection>
      ) : null}
      {hasAgents && hasAgencies ? <SpacingBlock mTop={5} /> : null}
      {hasAgents ? (
        <ContentSection>
          <ContentSectionTitle>{t('madad.localWinners.agentsTitle', { area })}</ContentSectionTitle>
          <AgentsCarousel agents={agents} />
        </ContentSection>
      ) : null}
      <SpacingBlock mTop={isMobile ? 3 : 5}>
        <LinkToMadadLanding />
      </SpacingBlock>
    </Root>
  );
};

export default LocalWinners;
